import {
  Flag,
  Person,
  Search,
  Clear,
  Event,
  ArrowRightAlt,
  AccessTime,
  Category,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  IconButton,
  InputBase,
  ListItemIcon,
  Menu,
  MenuItem,
  Paper,
  Popover,
  TextField,
  Typography,
  Divider,
  alpha,
  useTheme,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { severityColorMap, typeToIconMapping, getBackgroundColor } from "../utils";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import CheckIcon from "@mui/icons-material/Check";
import { useCreateBugMutation, useGetListOfMembersQuery } from "@/features/bughurd/api";
import { toast } from "react-toastify";
import { DatePickerPopover } from "./DatePickerPopover";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
const convertHoursToTimeString = (hours) => {
  if (!hours || hours === 0) return "";

  const days = Math.floor(hours / 12); // Assuming 8-hour workday
  const remainingHours = hours % 12;

  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (remainingHours > 0) parts.push(`${remainingHours}h`);

  return parts.join(" ");
};
export const SubtaskCreationCard = ({
  parentTask,
  onClose,
  onSave,
  refreshColumn,
  projectDetails,
}) => {
  const { t } = useTranslation(["bugherd"]);
  const theme = useTheme();
  const { id } = useParams();
  const [subtaskName, setSubtaskName] = useState("");
  const [assignee, setAssignee] = useState<{ id: string; name: string } | null>(null);
  const [dueDate, setDueDate] = useState("");
  const [priority, setPriority] = useState("");
  const [issueType, setIssueType] = useState("");
  const [dateAnchorEl, setDateAnchorEl] = useState(null);
  const [assigneeAnchorEl, setAssigneeAnchorEl] = useState(null);
  const [priorityAnchorEl, setPriorityAnchorEl] = useState(null);
  const [issueTypeAnchorEl, setIssueTypeAnchorEl] = useState(null);
  const [estimationAnchorEl, setEstimationAnchorEl] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Use project members API for better filtering
  const { data: companyMemberData } = useGetListOfMembersQuery({
    projectId: id || parentTask?.project?._id,
    page: 1,
    size: 100,
    searchName: "",
  });

  const [estimation, setEstimation] = useState<number | "">("");
  const [createBug, { isLoading: isCreatingBug }] = useCreateBugMutation();
  const inputRef = useRef(null);
  const cardRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Check if estimation is required from project details
  const isEstimationRequired = projectDetails?.isEstimationRequired || false;
  const estimationOptions = [2, 4, 8, 10, 12, 14];

  // Issue type options from project or default
  const defaultIssueTypes = [
    { value: "TASK", label: "Task", code: "TASK" },
    { value: "BUG", label: "Bug", code: "BUG" },
    { value: "STORY", label: "Story", code: "STORY" },
    { value: "EPIC", label: "Epic", code: "EPIC" },
  ];

  const issueTypeOptions = projectDetails?.itemTypes?.length
    ? projectDetails.itemTypes.map((type: any) => ({
        value: type.code || type.value,
        label: type.name || type.label || type.code,
        code: type.code || type.value,
      }))
    : defaultIssueTypes;

  // Get project filtered users (team members and leads only)
  const getProjectFilteredUsers = () => {
    return (
      companyMemberData?.data?.decryptedData?.items
        ?.filter(
          (item: any) =>
            item?.userRole === "TEAM_MEMBER" || item?.userRole === "TEAM_LEAD"
        )
        ?.map((item: any) => ({
          _id: item?.user?._id || item?._id,
          fullName:
            `${item?.user?.firstName || ""} ${item?.user?.lastName || ""}`.trim(),
          email: item?.user?.email,
          userRole: item?.userRole,
        })) || []
    );
  };

  const projectId = parentTask?.project?._id || parentTask?.project;

  // Format date to display
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) {
      return t("Today");
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return t("Tomorrow");
    } else {
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        weekday: "short",
      });
    }
  };

  // Get initials for avatar
  const getInitials = (name: string): string => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((part) => part[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Filter users for assignee dropdown using project filtered users
  const projectUsers = getProjectFilteredUsers();
  const filteredUsers = projectUsers?.filter((user: any) =>
    user.fullName?.toLowerCase().includes(searchQuery?.toLowerCase())
  );

  // Handle assigning user (single selection only)
  const handleAssigneeChange = (userId: string, userName: string) => {
    if (assignee?.id === userId) {
      // Deselect if clicking the same user
      setAssignee(null);
    } else {
      // Select new user
      setAssignee({ id: userId, name: userName });
    }
    setAssigneeAnchorEl(null);
  };

  // Handle issue type change
  const handleIssueTypeChange = (type: string) => {
    setIssueType(type);
    setIssueTypeAnchorEl(null);
  };

  // Get issue type icon
  const getIssueTypeIcon = (type: string) => {
    const typeInfo = typeToIconMapping[type as keyof typeof typeToIconMapping];
    if (!typeInfo) return <Category sx={{ fontSize: 14 }} />;
    return (
      <Box
        sx={{
          width: 16,
          height: 16,
          borderRadius: "3px",
          backgroundColor: typeInfo.bgcolor,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {typeInfo.icon}
      </Box>
    );
  };

  const handleDateSelect = (date) => {
    setDueDate(date);
    setDateAnchorEl(null);
  };

  const handlePriorityChange = (newPriority) => {
    setPriority(newPriority);
    setPriorityAnchorEl(null);
  };

  // ✅ Handle estimation change
  const handleEstimationChange = (value) => {
    setEstimation(value);
    setEstimationAnchorEl(null);
  };

  const handleSave = async (e: React.MouseEvent | React.FormEvent) => {
    e.stopPropagation();
    if (!subtaskName.trim() || isCreatingBug) return;

    // Validate issue type (required)
    if (!issueType) {
      toast.error(t("Issue type is required"));
      return;
    }

    // Validate estimation if required
    if (isEstimationRequired && !estimation) {
      toast.error(t("Estimation is required for this project"));
      return;
    }

    try {
      const payload = {
        summary: subtaskName,
        assignTo: assignee ? [assignee.id] : [],
        dueDate,
        severity: priority,
        parentBug: parentTask._id,
        type: issueType,
        project: parentTask?.project?._id || parentTask?.project,
        bugStatus: parentTask?.bugStatus?._id || parentTask?.bugStatus,
        ...(isEstimationRequired &&
          estimation && {
            estimation: convertHoursToTimeString(estimation),
          }),
      };

      const result = await createBug(payload).unwrap();
      refreshColumn();
      toast.success(t("Subtask created successfully"));

      // Call onSave callback if provided
      if (onSave) {
        onSave(result);
      }

      setSubtaskName("");
      setAssignee(null);
      setDueDate("");
      setPriority("");
      setIssueType("");
      setEstimation("");
      onClose();
    } catch (error) {
      console.error("Error creating subtask:", error);
      toast.error(t("Failed to create subtask"));
    }
  };

  const handleCardClick = (e) => {
    e.stopPropagation();
  };

  return (
    <Paper
      ref={cardRef}
      elevation={3}
      onClick={handleCardClick}
      sx={{
        borderRadius: "12px",
        border: "2px solid",
        borderColor: alpha(theme.palette.primary.main, 0.3),
        mb: 2,
        overflow: "visible",
        zIndex: 1,
        background: theme.palette.background.paper,
        transition: "all 0.2s ease",
        "&:hover": {
          borderColor: theme.palette.primary.main,
          boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.15)}`,
        },
      }}
    >
      <Box sx={{ p: 2.5 }}>
        {/* Header with input and save button */}
        <Box
          component="form"
          sx={{ display: "flex", alignItems: "center", mb: 2.5 }}
          onSubmit={(e) => {
            e.preventDefault();
            handleSave(e);
          }}
        >
          <TextField
            fullWidth
            placeholder={t("Subtask Name...")}
            variant="standard"
            value={subtaskName}
            onChange={(e) => setSubtaskName(e.target.value)}
            inputRef={inputRef}
            InputProps={{ disableUnderline: true }}
            sx={{
              fontSize: "16px",
              flex: 1,
              "& input": {
                fontSize: "15px",
                fontWeight: 500,
                padding: "8px 0",
              },
            }}
            onClick={(e) => e.stopPropagation()}
          />
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={
              !subtaskName.trim() ||
              !issueType ||
              isCreatingBug ||
              (isEstimationRequired && !estimation)
            }
            sx={{
              backgroundColor: theme.palette.primary.main,
              "&:hover": {
                backgroundColor: theme.palette.primary.dark,
              },
              "&:disabled": {
                backgroundColor: alpha(theme.palette.primary.main, 0.3),
              },
              ml: 1.5,
              borderRadius: "20px",
              px: 2.5,
              textTransform: "none",
              fontWeight: 600,
            }}
            endIcon={isCreatingBug ? null : <ArrowRightAlt />}
          >
            {isCreatingBug ? t("Saving...") : t("Save")}
          </Button>
        </Box>

        {/* Form fields grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: 1,
          }}
        >
          {/* Issue Type - REQUIRED */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              p: 1,
              borderRadius: 1.5,
              border: !issueType ? `1px dashed ${theme.palette.error.main}` : "1px solid",
              borderColor: !issueType ? theme.palette.error.main : alpha(theme.palette.divider, 0.5),
              backgroundColor: issueType
                ? alpha(theme.palette.primary.main, 0.04)
                : alpha(theme.palette.error.main, 0.02),
              transition: "all 0.2s ease",
              minWidth: 0,
              overflow: "hidden",
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                borderColor: theme.palette.primary.main,
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
              setIssueTypeAnchorEl(e.currentTarget);
            }}
          >
            <Category
              sx={{
                fontSize: 16,
                mr: 0.75,
                flexShrink: 0,
                color: !issueType ? theme.palette.error.main : theme.palette.text.secondary,
              }}
            />
            {issueType ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                {getIssueTypeIcon(issueType)}
                <Typography
                  sx={{
                    color: "text.primary",
                    fontWeight: 500,
                    fontSize: "12px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {issueTypeOptions.find((t: any) => t.value === issueType)?.label || issueType}
                </Typography>
              </Box>
            ) : (
              <Typography
                sx={{
                  color: theme.palette.error.main,
                  fontWeight: 500,
                  fontSize: "12px",
                  whiteSpace: "nowrap",
                }}
              >
                {t("Type")} *
              </Typography>
            )}
          </Box>

          {/* Assignee (Single) */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              p: 1,
              borderRadius: 1.5,
              border: "1px solid",
              borderColor: alpha(theme.palette.divider, 0.5),
              backgroundColor: assignee
                ? alpha(theme.palette.primary.main, 0.04)
                : "transparent",
              transition: "all 0.2s ease",
              minWidth: 0,
              overflow: "hidden",
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                borderColor: theme.palette.primary.main,
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
              setAssigneeAnchorEl(e.currentTarget);
            }}
          >
            {assignee ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                <Avatar
                  sx={{
                    width: 22,
                    height: 22,
                    fontSize: "9px",
                    fontWeight: 600,
                    background: getBackgroundColor(assignee.id, projectId),
                    flexShrink: 0,
                  }}
                >
                  {getInitials(assignee.name)}
                </Avatar>
                <Typography
                  sx={{
                    color: "text.primary",
                    fontSize: "12px",
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {assignee.name.split(" ")[0]}
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                }}
              >
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    background: "#f3f4f6",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Person
                    sx={{
                      fontSize: 14,
                      color: "#9ca3af",
                    }}
                  />
                </Box>
                <Typography sx={{ color: "text.secondary", fontSize: "12px" }}>
                  {t("Assignee")}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Due Date */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              p: 1,
              borderRadius: 1.5,
              border: "1px solid",
              borderColor: alpha(theme.palette.divider, 0.5),
              backgroundColor: dueDate
                ? alpha(theme.palette.primary.main, 0.04)
                : "transparent",
              transition: "all 0.2s ease",
              minWidth: 0,
              overflow: "hidden",
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                borderColor: theme.palette.primary.main,
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
              setDateAnchorEl(e.currentTarget);
            }}
          >
            <Event sx={{ fontSize: 16, mr: 0.75, flexShrink: 0, color: "text.secondary" }} />
            {dueDate ? (
              <Typography
                sx={{
                  color: "text.primary",
                  fontSize: "12px",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {formatDate(dueDate)}
              </Typography>
            ) : (
              <Typography sx={{ color: "text.secondary", fontSize: "12px" }}>
                {t("Date")}
              </Typography>
            )}
          </Box>

          {/* Priority */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              p: 1,
              borderRadius: 1.5,
              border: "1px solid",
              borderColor: alpha(theme.palette.divider, 0.5),
              backgroundColor: priority
                ? alpha(severityColorMap[priority] || theme.palette.grey[500], 0.08)
                : "transparent",
              transition: "all 0.2s ease",
              minWidth: 0,
              overflow: "hidden",
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                borderColor: theme.palette.primary.main,
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
              setPriorityAnchorEl(e.currentTarget);
            }}
          >
            <Flag sx={{ fontSize: 16, mr: 0.75, flexShrink: 0, color: "text.secondary" }} />
            {priority ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                <FiberManualRecordIcon
                  sx={{
                    fontSize: 8,
                    flexShrink: 0,
                    color: severityColorMap[priority] || "#9E9E9E",
                  }}
                />
                <Typography
                  sx={{
                    color: severityColorMap[priority] || "text.primary",
                    fontSize: "12px",
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {t(priority)}
                </Typography>
              </Box>
            ) : (
              <Typography sx={{ color: "text.secondary", fontSize: "12px" }}>
                {t("Priority")}
              </Typography>
            )}
          </Box>

          {/* Estimation Field - Only show if required */}
          {isEstimationRequired && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                p: 1,
                borderRadius: 1.5,
                border: !estimation ? `1px dashed ${theme.palette.warning.main}` : "1px solid",
                borderColor: !estimation
                  ? theme.palette.warning.main
                  : alpha(theme.palette.divider, 0.5),
                backgroundColor: estimation
                  ? alpha(theme.palette.primary.main, 0.04)
                  : alpha(theme.palette.warning.main, 0.02),
                transition: "all 0.2s ease",
                gridColumn: "span 2",
                minWidth: 0,
                overflow: "hidden",
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  borderColor: theme.palette.primary.main,
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                setEstimationAnchorEl(e.currentTarget);
              }}
            >
              <AccessTime sx={{ fontSize: 16, mr: 0.75, flexShrink: 0, color: "text.secondary" }} />
              {estimation ? (
                <Typography
                  sx={{ color: "text.primary", fontSize: "12px", fontWeight: 500 }}
                >
                  {estimation} {t("hours")}
                </Typography>
              ) : (
                <Typography
                  sx={{
                    color: theme.palette.warning.dark,
                    fontWeight: 500,
                    fontSize: "12px",
                  }}
                >
                  {t("Estimation")} *
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Date Picker Popover */}
      {dateAnchorEl && (
        <DatePickerPopover
          anchorEl={dateAnchorEl}
          onClose={() => setDateAnchorEl(null)}
          selectedDate={dueDate}
          onDateSelect={handleDateSelect}
        />
      )}

      {/* Priority Menu - Enhanced */}
      <Menu
        anchorEl={priorityAnchorEl}
        open={Boolean(priorityAnchorEl)}
        onClose={() => setPriorityAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            minWidth: 180,
            borderRadius: 2,
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {t("Select Priority")}
          </Typography>
        </Box>
        {[
          { value: "Highest", color: severityColorMap["Highest"] },
          { value: "High", color: severityColorMap["High"] },
          { value: "Medium", color: severityColorMap["Medium"] },
          { value: "Low", color: severityColorMap["Low"] },
        ].map((item) => (
          <MenuItem
            key={item.value}
            onClick={() => handlePriorityChange(item.value)}
            selected={priority === item.value}
            sx={{
              py: 1.5,
              mx: 1,
              my: 0.5,
              borderRadius: 1,
              "&.Mui-selected": {
                backgroundColor: alpha(item.color, 0.1),
                "&:hover": {
                  backgroundColor: alpha(item.color, 0.2),
                },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 28 }}>
              <FiberManualRecordIcon sx={{ color: item.color, fontSize: 14 }} />
            </ListItemIcon>
            <Typography
              variant="body2"
              sx={{
                color: item.color,
                fontWeight: priority === item.value ? 600 : 400,
              }}
            >
              {t(item.value)}
            </Typography>
            {priority === item.value && (
              <CheckIcon sx={{ ml: "auto", fontSize: 18, color: item.color }} />
            )}
          </MenuItem>
        ))}
      </Menu>

      {/* Estimation Menu - Enhanced */}
      <Menu
        anchorEl={estimationAnchorEl}
        open={Boolean(estimationAnchorEl)}
        onClose={() => setEstimationAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            minWidth: 200,
            borderRadius: 2,
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {t("Select Estimation")}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t("Choose time in hours")}
          </Typography>
        </Box>
        {estimationOptions.map((option) => (
          <MenuItem
            key={option}
            onClick={() => handleEstimationChange(option)}
            selected={estimation === option}
            sx={{
              py: 1.5,
              mx: 1,
              my: 0.5,
              borderRadius: 1,
              "&.Mui-selected": {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.2),
                },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <AccessTime sx={{ color: theme.palette.primary.main, fontSize: 18 }} />
            </ListItemIcon>
            <Typography variant="body2" sx={{ fontWeight: estimation === option ? 600 : 400 }}>
              {option} {t("hours")}
            </Typography>
            {estimation === option && (
              <CheckIcon sx={{ ml: "auto", fontSize: 18, color: theme.palette.primary.main }} />
            )}
          </MenuItem>
        ))}
      </Menu>

      {/* Issue Type Menu */}
      <Menu
        anchorEl={issueTypeAnchorEl}
        open={Boolean(issueTypeAnchorEl)}
        onClose={() => setIssueTypeAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            minWidth: 200,
            borderRadius: 2,
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {t("Select Issue Type")}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t("Required field")}
          </Typography>
        </Box>
        {issueTypeOptions.map((option: any) => (
          <MenuItem
            key={option.value}
            onClick={() => handleIssueTypeChange(option.value)}
            selected={issueType === option.value}
            sx={{
              py: 1.5,
              mx: 1,
              my: 0.5,
              borderRadius: 1,
              "&.Mui-selected": {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.2),
                },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              {getIssueTypeIcon(option.value)}
            </ListItemIcon>
            <Typography variant="body2" sx={{ fontWeight: issueType === option.value ? 600 : 400 }}>
              {option.label}
            </Typography>
            {issueType === option.value && (
              <CheckIcon sx={{ ml: "auto", fontSize: 18, color: theme.palette.primary.main }} />
            )}
          </MenuItem>
        ))}
      </Menu>

      {/* Assignee Popover - Single Selection */}
      <Popover
        anchorEl={assigneeAnchorEl}
        open={Boolean(assigneeAnchorEl)}
        onClose={() => {
          setAssigneeAnchorEl(null);
          setSearchQuery("");
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        PaperProps={{
          sx: { width: 300, maxHeight: 400, p: 1 },
        }}
      >
        <Box sx={{ p: 1, mb: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              background: "#f5f5f5",
              borderRadius: 1,
              p: 0.5,
              mb: 1,
            }}
          >
            <Search sx={{ color: "text.secondary", ml: 1, mr: 1 }} />
            <InputBase
              placeholder={t("Search people...")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ ml: 1, flex: 1 }}
              onClick={(e) => e.stopPropagation()}
            />
            {searchQuery && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setSearchQuery("");
                }}
              >
                <Clear fontSize="small" />
              </IconButton>
            )}
          </Box>

          {assignee && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t("Assigned")}
              </Typography>
              <MenuItem
                onClick={() => handleAssigneeChange(assignee.id, assignee.name)}
                sx={{
                  borderRadius: 1,
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  mb: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <Avatar
                    sx={{
                      width: 24,
                      height: 24,
                      mr: 1,
                      backgroundColor: getBackgroundColor(assignee.id, projectId),
                    }}
                  >
                    <Typography variant="subtitle2">
                      {getInitials(assignee.name)}
                    </Typography>
                  </Avatar>
                  <Typography sx={{ flexGrow: 1 }}>
                    {assignee.name}
                  </Typography>
                  <CheckIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                </Box>
              </MenuItem>
              <Divider sx={{ my: 1 }} />
            </>
          )}

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t("Suggestions")}
          </Typography>

          {filteredUsers
            ?.filter((user: any) => user._id !== assignee?.id)
            .map((user: any) => (
              <MenuItem
                key={user._id}
                onClick={() => handleAssigneeChange(user._id, user.fullName)}
                sx={{ borderRadius: 1 }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", width: "100%" }}
                >
                  <Avatar
                    sx={{
                      width: 24,
                      height: 24,
                      mr: 1,
                      backgroundColor: getBackgroundColor(user._id, projectId),
                    }}
                  >
                    <Typography variant="subtitle2">
                      {getInitials(user.fullName)}
                    </Typography>
                  </Avatar>
                  <Typography sx={{ flexGrow: 1 }}>{user.fullName}</Typography>
                </Box>
              </MenuItem>
            ))}

          {filteredUsers?.filter((user: any) => user._id !== assignee?.id).length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 1, textAlign: "center" }}>
              {searchQuery ? t("No users found") : t("No suggestions available")}
            </Typography>
          )}
        </Box>
      </Popover>
    </Paper>
  );
};
